import type { ReactNode } from 'react';
import type { LookGroupDTOType, LookGroupLookDTOType } from '../../../../schemas/styling';
import { getImageUrl } from '../../helpers';
import { getWorkspaceGeneratedImage, getWorkspaceSourceImage } from './utils';
import { ArrowFatRightIcon } from '@phosphor-icons/react';

type LookWorkspaceMediaProps = {
  look: LookGroupLookDTOType;
  group: LookGroupDTOType;
  workflowLine: string;
  activityControl?: ReactNode;
  publishControl?: ReactNode;
};

const LookWorkspaceMedia = ({
  look,
  group,
  workflowLine,
  activityControl,
  publishControl,
}: LookWorkspaceMediaProps) => {
  const sourceImage = getWorkspaceSourceImage(group);
  const generatedImage = getWorkspaceGeneratedImage(look);
  const statusLabel = (look.qc_status ?? 'pending').replace('_', ' ');
  const workflowLabel = group.occasion_name ? 'occasion' : 'standard';
  const productType = group.base_product_type?.trim();

  return (
    <div className="w-88 space-y-3">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-3">
        <div className="overflow-hidden rounded-sm bg-muted">
          {sourceImage ? (
            <img
              src={getImageUrl(sourceImage, 720)}
              alt={`Source for ${look.look_name || `Look ${look.id}`}`}
              className="aspect-2/3 w-full object-cover"
            />
          ) : (
            <div className="flex aspect-2/3 items-center justify-center text-[10px] text-muted-foreground">
              No source
            </div>
          )}
        </div>

        <div className="flex h-full shrink-0 items-center justify-center text-muted-foreground">
          <ArrowFatRightIcon size={16} fill="#171717" />
        </div>

        <div className="overflow-hidden rounded-sm bg-muted">
          {generatedImage ? (
            <img
              src={getImageUrl(generatedImage, 900)}
              alt={look.look_name || `Look ${look.id}`}
              className="aspect-2/3 w-full object-cover"
            />
          ) : (
            <div className="flex aspect-2/3 items-center justify-center text-[10px] text-muted-foreground">
              No image
            </div>
          )}
        </div>
      </div>

      {activityControl ? <div>{activityControl}</div> : null}

      <div className="space-y-1.5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-medium tracking-[0.12em] text-muted-foreground uppercase">
            <span>{statusLabel}</span>
            <span>•</span>
            <span>{workflowLabel}</span>
            {productType ? (
              <>
                <span>•</span>
                <span>{productType}</span>
              </>
            ) : null}
          </div>

          {publishControl}
        </div>

        <p className="m-0 text-[10px] font-medium tracking-[0.12em] text-muted-foreground uppercase">
          Look #{look.id}
        </p>

        <h3 className="m-0 text-[1.1rem] font-semibold tracking-tight text-foreground">
          {look.look_name || look.look_hook || `Look ${look.id}`}
        </h3>

        {workflowLine ? <p className="m-0 text-sm text-muted-foreground">{workflowLine}</p> : null}

        {look.look_hook ? (
          <p className="m-0 line-clamp-4 text-sm leading-6 text-muted-foreground">
            {look.look_hook}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default LookWorkspaceMedia;
